// Bouton « Comparer DAL ↔ Devis » — déclenche AG-02 en mode comparaison
// uniquement sur clic explicite (ne se lance JAMAIS automatiquement).
//
// Bug corrigé 2026-05-11 : les alertes DAL↔devis ne doivent apparaître QUE
// quand ce bouton est cliqué — jamais pendant l'analyse DAL seule par AG-02.
// Le system_prompt AG-02 interdit désormais ces alertes côté agent.
import { useState } from "react";
import {
  ShieldCheck, Loader2, CheckCircle2, AlertTriangle, XCircle,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

const VERDICTS = {
  conforme: {
    label: "Conforme",
    bg: "rgba(16,185,129,0.12)",
    color: "#10b981",
    icon: CheckCircle2,
  },
  ecart_mineur: {
    label: "Écart mineur",
    bg: "rgba(245,158,11,0.12)",
    color: "var(--oh-stat-amber-text)",
    icon: AlertTriangle,
  },
  non_conforme: {
    label: "Non conforme ANAH",
    bg: "rgba(239,68,68,0.12)",
    color: "var(--oh-stat-red-text)",
    icon: XCircle,
  },
};

export function VerifyDevisDALButton({
  dossierId,
  tenantId,
  hasDevis,
  size = "sm",
  variant = "outline",
  className = "",
  onResult,
}) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const verify = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const headers = {
        "Content-Type": "application/json",
        "X-Tenant-ID": tenantId || "tef",
        ...(token && { Authorization: `Bearer ${token}` }),
      };
      const res = await fetch(`${API}/api/dal/verify-devis/${dossierId}`, {
        method: "POST", headers, credentials: "include",
      });
      const data = await res.json();
      if (data.success) {
        setResult(data.verification);
        onResult?.(data.verification);
      } else {
        toast.error(data.error || "Erreur de vérification");
      }
    } catch (e) {
      toast.error(`Erreur réseau : ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!result) {
    return (
      <Button
        size={size}
        variant={variant}
        onClick={verify}
        disabled={loading || !hasDevis}
        className={className}
        data-testid="btn-compare-dal-devis"
        style={{
          borderColor: "var(--oh-border)",
          color: "var(--oh-text-secondary)",
        }}
        title={!hasDevis
          ? "Aucun devis chargé — uploader d'abord un devis"
          : "Claude Vision analyse le devis vs préconisations DAL (cohérence + écarts + montants)"}
      >
        {loading
          ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" />
          : <ShieldCheck className="w-3.5 h-3.5 mr-2" />}
        {loading ? "Analyse en cours..." : "Analyser le devis IA"}
      </Button>
    );
  }

  const V = VERDICTS[result.verdict] || VERDICTS.ecart_mineur;
  return (
    <div
      className="rounded-lg p-3 space-y-2"
      style={{
        background: "var(--oh-bg-surface)",
        border: "1px solid var(--oh-border)",
      }}
      data-testid="verify-devis-dal-result"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge
            className="text-xs px-3 py-1 border-0 flex items-center gap-1.5"
            style={{ background: V.bg, color: V.color }}
          >
            <V.icon className="w-3.5 h-3.5" />
            {V.label}
          </Badge>
          {result.score != null && (
            <span className="text-xs font-mono" style={{ color: "var(--oh-text-muted)" }}>
              {result.score}%
            </span>
          )}
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setResult(null)}
          className="h-7 text-xs"
          style={{ color: "var(--oh-text-muted)" }}
        >
          Relancer
        </Button>
      </div>
      {result.synthese && (
        <p className="text-xs" style={{ color: "var(--oh-text-secondary)" }}>
          {result.synthese}
        </p>
      )}
      {result.preconisations_non_couvertes?.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wide mb-1" style={{ color: "var(--oh-stat-red-text)" }}>
            Préconisations DAL absentes du devis
          </p>
          {result.preconisations_non_couvertes.map((p, i) => (
            <p key={i} className="text-xs" style={{ color: "var(--oh-text)" }}>
              • {p}
            </p>
          ))}
        </div>
      )}
      {result.preconisations_couvertes?.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wide mb-1" style={{ color: "#10b981" }}>
            Préconisations couvertes
          </p>
          {result.preconisations_couvertes.map((p, i) => (
            <p key={i} className="text-xs" style={{ color: "var(--oh-text)" }}>
              ✓ {p}
            </p>
          ))}
        </div>
      )}
      {result.prestations_hors_dal?.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wide mb-1" style={{ color: "var(--oh-stat-amber-text)" }}>
            Postes du devis hors DAL
          </p>
          {result.prestations_hors_dal.map((p, i) => (
            <p key={i} className="text-xs" style={{ color: "var(--oh-text)" }}>
              • {p}
            </p>
          ))}
        </div>
      )}
      {result.recommandations?.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wide mb-1" style={{ color: "var(--oh-text-muted)" }}>
            Recommandations
          </p>
          {result.recommandations.map((r, i) => (
            <p key={i} className="text-xs" style={{ color: "var(--oh-text-secondary)" }}>
              → {r}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

export default VerifyDevisDALButton;
